import React, { useRef } from 'react';
import { connect } from 'react-redux';

import styled from 'styled-components';

import amplitude from 'amplitude-js';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { IconWrapper } from 'apps/auth/components/common/Panel/styles';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { StackButton } from 'apps/auth/components/common/StackButton/StackButton';
import { Text } from 'apps/auth/components/common/Text/Text';
import PaysendPlusBanner from 'apps/auth/components/paysendPlus/PaysendPlusBanner';
import { ADD_MONEY_SELECTED, EXCHANGE_SELECTED, INCREASE_LIMITS_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { ACCOUNT_CURRENCY, FROM, GA_LEVEL, IS_LINKED_TO_CARD } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { ACCOUNT, AML_LEVEL_ADVANCE } from 'apps/auth/constants/Constants';
import { AUTH_EMAIL_GA_CONFIRM_PREVIEW, AUTH_GA_ACCOUNTS, AUTH_GA_POCKET, AUTH_GA_STUB_PAGE } from 'apps/auth/constants/UrlConstants';
import { getLocalizedRawAmount } from 'apps/auth/helpers/formats';
import { toLowerCase, getCurrencyIcon, onIconRenderError } from 'apps/auth/helpers/utils';
import { useApp, useLanguage } from 'apps/auth/hooks/selectors';

import GaPocketAccountDetails from './GaPocketAccountDetails';
import GaPocketCardList from './GaPocketCardList';

const StackButtonsCarousel = styled.div`
    margin: 0 -16px;
    -webkit-overflow-scrolling: touch;
    overflow-x: auto;
    font-size: 14px;
    line-height: 20px;
    display: flex;
    flex-direction: row;
`;
const StackButtonsContainer = styled.div`
    width: 33%;
    min-width: 116px;
`;

const GaPocket = (props) => {
    const popupRef = useRef(null);
    const expectedGALevel = props.currentLevel + 1;
    const { country } = useApp();
    const { lang } = useLanguage();

    const amplitudeEventForTopUp = () => {
        amplitude.getInstance().logEvent(ADD_MONEY_SELECTED, {
            [ACCOUNT_CURRENCY]: props.pocketCurrencyCode,
            [IS_LINKED_TO_CARD]: props.hasLinkedPlasticCard || props.hasLinkedVirtualCard,
            [FROM]: ACCOUNT,
        });
        if (props.currentLevel < AML_LEVEL_ADVANCE) {
            amplitude.getInstance().logEvent(INCREASE_LIMITS_SHOWN, { [GA_LEVEL]: expectedGALevel });
        }
    };

    const amplitudeEventForExchange = () => {
        amplitude.getInstance().logEvent(EXCHANGE_SELECTED, {
            [ACCOUNT_CURRENCY]: props.pocketCurrencyCode,
        });
    };

    const addMoneyButton = () => {
        let link;
        const ACTION_FOR_TOP_UP = '?action=add';
        if (props.needConfirmEmail && props.accountBalance > 0) {
            link = AUTH_EMAIL_GA_CONFIRM_PREVIEW + ACTION_FOR_TOP_UP;
        } else if (props.canTopUp) {
            link = `${AUTH_GA_POCKET}${props.accountId}/top-up`;
        }

        return (
            <StackButton
                type="reactLink"
                to={link ? link : '#'}
                isDisabled={!link}
                buttonIcon="plus"
                buttonCaption={props.addMoney}
                onClick={() => {
                    amplitudeEventForTopUp();
                }}
            />
        );
    };
    const sendMoneyButton = () => {
        let link;
        if (props.accountBalance > 0) {
            link = props.canSendExchangeFromAccount ? `/auth/ga/pocket/${props.accountId}/send` : AUTH_GA_STUB_PAGE;
        }
        return <StackButton type="reactLink" to={link ? link : '#'} isDisabled={!link} buttonIcon="arrow-up-right" buttonCaption={props.sendMoney} />;
    };
    const exchangeMoneyButton = () => {
        let link;

        if (props.accountBalance > 0) {
            link = props.canSendExchangeFromAccount ? `/auth/ga/pocket/${props.accountId}/exchange` : AUTH_GA_STUB_PAGE;
        }
        return (
            <StackButton
                type="link"
                href={link ? link : '#'}
                isDisabled={!link}
                buttonIcon="arrow-exchange"
                buttonCaption={props.exchangeMoney}
                onClick={() => {
                    amplitudeEventForExchange();
                }}
            />
        );
    };

    return (
        <>
            <CommonPage backUrl={AUTH_GA_ACCOUNTS}>
                <Row indent="24px">
                    <Row indent="16px">
                        <DiskBadge
                            diskSize="large"
                            src={getCurrencyIcon(toLowerCase(props.pocketCurrencyCode))}
                            alt={props.flagIconName}
                            onError={onIconRenderError}
                        />
                    </Row>
                    <Text indent="4px" variant="div">
                        {props.pocketCurrencyTitle}
                    </Text>
                    <Text size="display1" variant="div">
                        {getLocalizedRawAmount(lang, country, props.accountBalance)}{' '}
                        <Text size="display1" smallCaps>
                            {toLowerCase(props.pocketCurrencyCode)}
                        </Text>
                    </Text>
                </Row>
                <StackButtonsCarousel>
                    <StackButtonsContainer>{addMoneyButton()}</StackButtonsContainer>
                    <StackButtonsContainer>{sendMoneyButton()}</StackButtonsContainer>
                    <StackButtonsContainer>{exchangeMoneyButton()}</StackButtonsContainer>
                </StackButtonsCarousel>
                <Separator />
                {props.hasCardTab && (
                    <>
                        <GaPocketCardList popupRef={popupRef} />
                        <Separator />
                    </>
                )}
                {props.paysendPlusBannerEnabled && (
                    <Row indent="16px">
                        <Row flex justify="center">
                            <PaysendPlusBanner />
                        </Row>
                        <Separator />
                    </Row>
                )}
                <GaPocketAccountDetails popupRef={popupRef} />
                <Panel type="reactLink" to={`${AUTH_GA_POCKET}${props.accountId}/activity`} hasIcon isHoverable negativeMargin>
                    <IconWrapper>
                        <Icon name="list-marked" color="primary" />
                    </IconWrapper>
                    <Text>{props.accountActivity}</Text>
                </Panel>
            </CommonPage>
            <PopupNew ref={popupRef} />
        </>
    );
};
const mapStateToProps = (state) => {
    return {
        canTopUp: state.extra.canTopUp,
        hasCardTab: state.auth.hasCardTab,
        accountBalance: state.extra?.account?.balance,
        accountId: state.extra?.account?.id,
        pocketCurrencyCode: state.extra?.account?.pocketCurrency?.code,
        pocketCurrencyTitle: state.extra?.account?.pocketCurrency?.title,
        currentLevel: state.extra?.currentLevel,
        addMoney: state.messages.pageMessages.addMoney,
        sendMoney: state.messages.pageMessages.sendMoney,
        exchangeMoney: state.messages.pageMessages.exchangeMoney,
        accountActivity: state.messages.pageMessages.accountActivity,
        needConfirmEmail: state.extra.needConfirmEmail,
        canSendExchangeFromAccount: state.extra.canSendExchangeFromAccount,
        hasLinkedPlasticCard: state.extra.hasLinkedPlasticCard,
        hasLinkedVirtualCard: state.extra.hasLinkedVirtualCard,
        paysendPlusBannerEnabled: state.extra.paysendPlusBannerEnabled,
    };
};
export default connect(mapStateToProps)(GaPocket);
